// research.jsx — the Research workspace: collect sources → build a source sheet / shiur outline.
const { useState: useStateRS, useRef: useRefRS, useEffect: useEffectRS } = React;

const srcByN = (n) => window.WS && window.WS.sourceByToken ? window.WS.sourceByToken(n) : (window.SOURCES || []).find((s) => s.n === n);
const srcForBlock = (block) => window.WS && window.WS.sourceFromBlock ? window.WS.sourceFromBlock(block) : srcByN(block && block.srcN);
const srcKey = (src) => window.WS && window.WS.sourceKey ? window.WS.sourceKey(src) : String((src && (src.id || src.n)) || "");

const TEACHING_KINDS = {
  question: { label: "Question", icon: "spark" },
  answer: { label: "Answer", icon: "check" },
  teaching: { label: "Teaching point", icon: "note" },
  transition: { label: "Transition", icon: "path" },
  instruction: { label: "Learning direction", icon: "book" },
  note: { label: "Comment", icon: "note" },
};

// The references are curated; the Hebrew and English are always fetched from
// the real corpus when a teacher opens an example.
const SAMPLE_SHIURIM = [
  {
    id: "ten-utterances",
    eyebrow: "Likkutei Sichos · Mishnah · Chumash",
    title: "Ten Utterances: Why Creation Has Weight",
    summary: "See how a Sicha begins with a Mishnah, sharpens its question, and returns to the opening of Torah.",
    duration: "25–35 min",
    sources: [
      { ref: "Likkutei Sichos Vol 30, בראשית, Sicha 1, Section 1", title: "Likkutei Sichos, Vol. 30", type: "Chassidus", relationship: "The Rebbe’s question and framework", take: 2 },
      { ref: "Pirkei Avot 5:1", title: "Pirkei Avot", type: "Mishnah", relationship: "Foundational Mishnah quoted by the Sicha" },
      { ref: "Genesis 1:1", title: "Bereishis", type: "Tanach", relationship: "The Torah’s opening statement of creation" },
    ],
    blocks: [
      { type: "heading", text: "Begin inside the Mishnah" },
      { type: "source", source: 1, note: "Read the Mishnah closely: it asks why creation unfolded through ten utterances rather than one." },
      { type: "comment", kind: "question", text: "If Hashem could create the world with one utterance, what do the ten utterances add?" },
      { type: "heading", text: "Let the Sicha sharpen the problem" },
      { type: "source", source: 0, note: "Notice how the Rebbe connects the Mishnah’s language to Hashem’s essential goodness." },
      { type: "comment", kind: "teaching", text: "The Sicha does not treat the number ten as a detail. It makes the moral meaning of creation itself the question." },
      { type: "heading", text: "Return to the beginning" },
      { type: "source", source: 2, note: "Use the first pasuk as the broad frame: the discussion is about the purpose and responsibility built into the created world." },
      { type: "comment", kind: "transition", text: "Now return to the next section of the Sicha and trace how its answer develops." },
    ],
  },
  {
    id: "inner-mikdash",
    eyebrow: "Maamar · Tehillim · Chumash",
    title: "The Beis HaMikdash Within Every Jew",
    summary: "Build a maamar-based shiur that moves from a verse in Tehillim to the Mishkan and the point of the Jewish heart.",
    duration: "30–40 min",
    sources: [
      { ref: "Sefer HaMaamarim Melukat Vol 1, מאמרים מלוקטים חלק א, Maamar 1, Section 1", title: "Sefer HaMaamarim Melukat, Vol. 1", type: "Chassidus", relationship: "The maamar’s opening question and explanation", take: 1 },
      { ref: "Psalms 47:5", title: "Tehillim", type: "Tanach", relationship: "The maamar’s opening verse" },
      { ref: "Exodus 25:8", title: "Shemos", type: "Tanach", relationship: "The command to make a sanctuary" },
    ],
    blocks: [
      { type: "heading", text: "The opening pasuk" },
      { type: "source", source: 1, note: "Read the phrase ‘the pride of Yaakov’ and ask what inheritance the pasuk describes." },
      { type: "comment", kind: "question", text: "Why is the Beis HaMikdash called Yaakov’s if the Mikdash belongs to Hashem?" },
      { type: "heading", text: "The maamar’s two readings" },
      { type: "source", source: 0, note: "Track the connection between the future Beis HaMikdash and the inner sanctuary in every Jew." },
      { type: "heading", text: "The pasuk that opens the inner reading" },
      { type: "source", source: 2, note: "Focus on ‘I will dwell within them’ as the bridge from the physical Mikdash to each person." },
      { type: "comment", kind: "teaching", text: "Show the class how one phrase can carry both a national promise and a personal demand for avodah." },
    ],
  },
  {
    id: "ahavas-yisrael",
    eyebrow: "Likkutei Sichos · Chumash · Gemara",
    title: "Ahavas Yisrael Beyond a Reason",
    summary: "Connect the Torah’s command, Hillel’s formulation, and the Rebbe’s explanation of unconditional love.",
    duration: "20–30 min",
    sources: [
      { ref: "Likkutei Sichos Vol 32, הוספות, Sicha 11, Section 11", title: "Likkutei Sichos, Vol. 32", type: "Chassidus", relationship: "The Rebbe’s definition of unconditional Ahavas Yisrael", take: 1 },
      { ref: "Leviticus 19:18", title: "Vayikra", type: "Tanach", relationship: "The Torah’s command to love another as oneself" },
      { ref: "Shabbat 31a:6", title: "Shabbos 31a", type: "Talmud", relationship: "Hillel’s practical formulation of responsibility to another" },
    ],
    blocks: [
      { type: "heading", text: "Begin with the mitzvah" },
      { type: "source", source: 1, note: "Ask what ‘as yourself’ demands beyond ordinary friendship." },
      { type: "comment", kind: "question", text: "Can love of another truly be as natural and unconditional as love of oneself?" },
      { type: "heading", text: "A rule for action" },
      { type: "source", source: 2, note: "Hillel translates the principle into a concrete test for conduct." },
      { type: "heading", text: "The Rebbe’s inner explanation" },
      { type: "source", source: 0, note: "Compare reason-dependent love with the essential self-love described in the Sicha." },
      { type: "comment", kind: "teaching", text: "The source chain moves from command, to behavior, to an explanation of the kind of love Torah asks a Jew to uncover." },
    ],
  },
];

function researchPlainText(html) {
  const holder = document.createElement("div");
  holder.innerHTML = String(html || "");
  holder.querySelectorAll("i.footnote, sup.footnote-marker, [data-overlay]").forEach((node) => node.remove());
  return (holder.textContent || "").replace(/\*{1,3}/g, "").replace(/\s+/g, " ").trim();
}

function researchRefKey(value) {
  return String(value || "").replace(/_/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
}

async function hydrateSample(sample) {
  const hydrated = await Promise.all(sample.sources.map(async (spec, index) => {
    const response = await fetch(`/bff/text/${encodeURIComponent(spec.ref.replace(/ /g, "_"))}`);
    if (!response.ok) throw new Error(`Could not load ${spec.title || spec.ref}`);
    const payload = await response.json();
    const segments = Array.isArray(payload.segments) ? payload.segments : [];
    const exact = segments.find((segment) => researchRefKey(segment.ref) === researchRefKey(spec.ref));
    const chosen = exact ? [exact] : segments.slice(0, Math.max(1, spec.take || 1));
    if (!chosen.length) throw new Error(`No text is available for ${spec.title || spec.ref}`);
    return {
      n: index + 1,
      id: `sample-${sample.id}-${index + 1}`,
      srcKey: `sample:${sample.id}:${spec.ref}`,
      type: spec.type || "Source",
      category: spec.type || "Source",
      displayCategory: spec.type || "Source",
      title: spec.title || payload.title || spec.ref,
      citation: spec.ref,
      relationship: spec.relationship || "Source in the teaching chain",
      he: chosen.map((segment) => researchPlainText(segment.he)).filter(Boolean).join(" "),
      en: chosen.map((segment) => researchPlainText(segment.en)).filter(Boolean).join(" "),
      srcRef: spec.ref,
      anchorRef: sample.sources[0].ref,
      anchorRefs: [sample.sources[0].ref],
      sample: true,
    };
  }));
  return hydrated;
}

function researchSourceNumber(ref) {
  let hash = 0;
  String(ref || "").split("").forEach((char) => { hash = ((hash << 5) - hash + char.charCodeAt(0)) | 0; });
  return 1000 + (Math.abs(hash) % 8999);
}

async function hydrateResearchSource(hit) {
  const ref = String((hit && (hit.ref || hit.title)) || "").trim();
  if (!ref) throw new Error("Choose a source first");
  const response = await fetch(`/bff/text/${encodeURIComponent(ref.replace(/ /g, "_"))}`);
  if (!response.ok) throw new Error(`Could not load ${ref}`);
  const payload = await response.json();
  const segments = Array.isArray(payload.segments) ? payload.segments : [];
  const exact = segments.find((segment) => researchRefKey(segment.ref) === researchRefKey(ref));
  const chosen = exact || segments[0];
  if (!chosen) throw new Error(`No text is available for ${ref}`);
  const category = (hit && (hit.genre || hit.category)) || (payload.categories || []).slice(-1)[0] || payload.primaryCategory || "Source";
  const title = (hit && hit.title) || payload.title || ref.replace(/\s+\d.*$/, "");
  return {
    n: researchSourceNumber(ref),
    id: `research-${researchSourceNumber(ref)}`,
    srcKey: `research:${ref}`,
    type: category,
    category,
    displayCategory: category,
    title,
    citation: ref,
    relationship: "Added from the Torah library",
    he: researchPlainText(chosen.he),
    en: researchPlainText(chosen.en),
    srcRef: ref,
    anchorRef: ref,
    anchorRefs: [ref],
    research: true,
  };
}

function sheetHasSource(sheet, sourceOrRef) {
  const wanted = typeof sourceOrRef === "string" ? researchRefKey(sourceOrRef) : researchRefKey((sourceOrRef && (sourceOrRef.srcRef || sourceOrRef.citation)) || "");
  return !!wanted && sheet.blocks.some((block) => {
    if (block.type !== "source") return false;
    const source = srcForBlock(block);
    return researchRefKey(source && (source.srcRef || source.citation)) === wanted;
  });
}

function ResearchSourceFinder({ ws, onClose, onOpenReader, onNotice, activeWorking }) {
  const [query, setQuery] = useStateRS("");
  const [results, setResults] = useStateRS([]);
  const [loading, setLoading] = useStateRS(false);
  const [error, setError] = useStateRS("");
  const [adding, setAdding] = useStateRS("");
  const sheet = window.activeSheet(ws);

  useEffectRS(() => {
    const value = query.trim();
    if (value.length < 2) { setResults([]); setLoading(false); setError(""); return; }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true); setError("");
      fetch(`/bff/search?q=${encodeURIComponent(value)}&limit=12`, { signal: controller.signal })
        .then((response) => response.ok ? response.json() : Promise.reject(new Error("Library search is unavailable")))
        .then((payload) => {
          const hits = Array.isArray(payload) ? payload : [];
          const direct = /\d/.test(value) && !hits.some((hit) => researchRefKey(hit.ref) === researchRefKey(value))
            ? [{ ref: value, title: value, type: "ref", direct: true }, ...hits]
            : hits;
          setResults(direct.slice(0, 12));
        })
        .catch((searchError) => { if (searchError.name !== "AbortError") setError(searchError.message || "Library search is unavailable"); })
        .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    }, 260);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [query]);

  const addResult = async (hit) => {
    const ref = hit && hit.ref;
    if (!ref || adding) return;
    if (sheetHasSource(sheet, ref)) { onNotice && onNotice(`${ref} is already in this sheet`); return; }
    setAdding(ref); setError("");
    try {
      const source = await hydrateResearchSource(hit);
      window.WS.collect(source);
      window.WS.addSourceBlock(source);
      onNotice && onNotice(`${ref} added to the sheet`);
      onClose && onClose();
    } catch (addError) {
      setError(addError && addError.message ? addError.message : "Could not add this source");
    } finally {
      setAdding("");
    }
  };

  const addWorking = async (hit) => {
    const ref = hit && hit.ref;
    if (!ref || adding) return;
    setAdding(`working:${ref}`); setError("");
    try {
      const source = await hydrateResearchSource(hit);
      window.WS.addWorkingText({ ...source, relationship: "Working text for research" });
      onNotice && onNotice(`${ref} added as a working text`);
    } catch (addError) {
      setError(addError && addError.message ? addError.message : "Could not add this working text");
    } finally {
      setAdding("");
    }
  };

  return (
    <div className="rs-rail-sect rs-finder">
      <div className="rs-rail-h">Discover sources <span>{activeWorking ? `Alongside ${activeWorking.title}` : "Whole library"}</span></div>
      <label className="rs-findbox">
        <Icon d="search" size={15} />
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Title, reference, Hebrew, or words…" aria-label="Search the Torah library" />
        {query && <button onClick={() => setQuery("")} aria-label="Clear search"><Icon d="close" size={13} /></button>}
      </label>
      {!query.trim() && <p className="rs-rail-hint rs-find-hint">Search by idea, title, phrase, or exact reference. Keep a text as research context, or add an exact passage to the sheet.</p>}
      {loading && <div className="rs-find-state"><span className="account-spinner" /> Searching the library…</div>}
      {error && <div className="rs-find-error" role="alert">{error}</div>}
      {!loading && query.trim().length >= 2 && !error && results.length === 0 && <div className="rs-find-state">No matching source found.</div>}
      {results.length > 0 && (
        <div className="rs-find-results">
          {results.map((hit, index) => {
            const inSheet = sheetHasSource(sheet, hit.ref);
            return (
              <article className="rs-find-result" key={`${hit.ref}-${index}`}>
                <button className="rs-find-open" onClick={() => onOpenReader && onOpenReader(hit.ref)} title="Open in Reader">
                  <b>{hit.title || hit.ref}</b>
                  {hit.heTitle && <span lang="he" dir="rtl" className="heb">{hit.heTitle}</span>}
                  <i>{hit.ref}</i>
                  {hit.direct && <em>Exact reference</em>}
                  {hit.excerpt && <p>{researchPlainText(hit.excerpt)}</p>}
                </button>
                <div className="rs-find-actions">
                  <button className="rs-find-work" disabled={adding === `working:${hit.ref}`} onClick={() => addWorking(hit)} title="Use this text as research context without adding it to the sheet">
                    <Icon d="layers" size={13} /> {adding === `working:${hit.ref}` ? "Saving…" : "Work from"}
                  </button>
                  <button className={`rs-find-add${inSheet ? " is-added" : ""}`} disabled={inSheet || adding === hit.ref} onClick={() => addResult(hit)}>
                    <Icon d={inSheet ? "check" : "plus"} size={13} /> {inSheet ? "Added" : adding === hit.ref ? "Adding…" : "Add passage"}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

// auto-growing textarea
function AutoText({ value, onChange, placeholder, className }) {
  const ref = useRefRS(null);
  const fit = () => { const el = ref.current; if (el) { el.style.height = "auto"; el.style.height = el.scrollHeight + "px"; } };
  useEffectRS(() => { fit(); }, [value]);
  return (
    <textarea ref={ref} className={className} value={value} placeholder={placeholder}
      onChange={(e) => { onChange(e.target.value); fit(); }} rows={1} />
  );
}

// ── left rail: collected sources + add controls ───────────────────────────────
function CollectRail({ ws, onClose, isMobile, onOpenReader, onNotice }) {
  const [browse, setBrowse] = useStateRS(false);
  const [workingSet, setWorkingSet] = useStateRS("all");
  const sheet = window.activeSheet(ws);
  const collected = ws.collected.map(srcByN).filter(Boolean);
  const workingTexts = (sheet.workingTexts || []).map(srcByN).filter(Boolean);
  const activeWorking = workingSet === "all" ? null : workingTexts.find((source) => srcKey(source) === workingSet) || null;
  const all = window.SOURCES || [];
  const addCollected = (source) => {
    if (sheetHasSource(sheet, source)) { onNotice && onNotice(`${source.citation || source.title} is already in this sheet`); return; }
    window.WS.addSourceBlock(source);
    onNotice && onNotice(`${source.citation || source.title} added to the sheet`);
    onClose && onClose();
  };
  return (
    <div className="rs-rail">
      <div className="rs-rail-sect rs-working-set">
        <div className="rs-rail-h">Working texts <span>{workingTexts.length || "Optional"}</span></div>
        <p className="rs-rail-hint">Keep several maamarim or sefarim in view. Nothing enters the sheet until you choose an exact passage.</p>
        <div className="rs-working-chips">
          <button className={workingSet === "all" ? "on" : ""} onClick={() => setWorkingSet("all")}>{workingTexts.length ? "All research" : "Start from research"}</button>
          {workingTexts.map((source) => <button key={srcKey(source)} className={workingSet === srcKey(source) ? "on" : ""} onClick={() => setWorkingSet(srcKey(source))}>{source.title}</button>)}
        </div>
        {activeWorking && <div className="rs-working-active"><span><b>{activeWorking.title}</b><i>{activeWorking.citation}</i></span><button onClick={() => onOpenReader && onOpenReader(activeWorking.srcRef || activeWorking.citation)}>Open</button><button aria-label="Remove working text" onClick={() => { window.WS.removeWorkingText(activeWorking); setWorkingSet("all"); }}><Icon d="close" size={12} /></button></div>}
      </div>
      <ResearchSourceFinder ws={ws} onClose={onClose} onOpenReader={onOpenReader} onNotice={onNotice} activeWorking={activeWorking} />

      <div className="rs-rail-sect">
        <div className="rs-rail-h">Add to sheet</div>
        <div className="rs-add-btns rs-add-grid">
          <button className="rs-add-btn" onClick={() => { window.WS.addHeading(); onClose && onClose(); }}><Icon d="text" size={15} /> Heading</button>
          <button className="rs-add-btn" onClick={() => { window.WS.addComment("", "question"); onClose && onClose(); }}><Icon d="spark" size={15} /> Question</button>
          <button className="rs-add-btn" onClick={() => { window.WS.addComment("", "answer"); onClose && onClose(); }}><Icon d="check" size={15} /> Answer</button>
          <button className="rs-add-btn" onClick={() => { window.WS.addComment("", "note"); onClose && onClose(); }}><Icon d="note" size={15} /> Comment</button>
          <button className="rs-add-btn" onClick={() => { window.WS.addComment("", "teaching"); onClose && onClose(); }}><Icon d="spark" size={15} /> Teaching point</button>
        </div>
      </div>

      <div className="rs-rail-sect">
        <div className="rs-rail-h">Collected sources <span>{collected.length}</span></div>
        {collected.length === 0 && <p className="rs-rail-hint">Sources you collect in the Reader or find above will stay here for reuse.</p>}
        <div className="rs-collected">
          {collected.map((s) => {
            const inSheet = sheetHasSource(sheet, s);
            return (
            <div key={s.id} className="rs-coll-item" style={{ borderLeft: `3px solid ${typeVar(s.type)}` }}>
              <button className="rs-coll-main" disabled={inSheet} onClick={() => addCollected(s)}>
                <span className="srccard-n" style={{ background: typeVar(s.type) }}>{s.n}</span>
                <span className="rs-coll-txt">
                  <b>{s.title} <span className="srccard-cite">{s.citation}</span></b>
                  <span className="srccard-rel"><TypeDot type={s.type} size={6} /> {s.relationship}</span>
                </span>
                <span className={`rs-coll-action${inSheet ? " is-added" : ""}`}><Icon d={inSheet ? "check" : "plus"} size={13} /> {inSheet ? "Added" : "Add"}</span>
              </button>
              <button className="rs-coll-remove" title="Remove from collected sources" onClick={() => window.WS.uncollect(s)}><Icon d="close" size={12} /></button>
            </div>
          );})}
        </div>
      </div>

      <div className="rs-rail-sect">
        <button className="rs-browse-toggle" onClick={() => setBrowse(!browse)}>
          <Icon d="book" size={14} /> Sources from the current Reader text
          <Icon d="chevd" size={14} style={{ marginLeft: "auto", transform: browse ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
        </button>
        {browse && (
          <div className="rs-browse">
            {all.map((s) => {
              const inSheet = sheet.blocks.some((b) => b.type === "source" && srcKey(srcForBlock(b)) === srcKey(s));
              return (
                <button key={srcKey(s)} className="rs-browse-item" disabled={inSheet} onClick={() => { window.WS.addSourceBlock(s); window.WS.collect(s); onNotice && onNotice(`${s.citation || s.title} added to the sheet`); onClose && onClose(); }}>
                  <TypeDot type={s.type} size={7} />
                  <span><b>{s.title}</b> {s.citation}</span>
                  {inSheet ? <Icon d="check" size={14} style={{ marginLeft: "auto", color: "var(--accent)" }} /> : <Icon d="plus" size={14} style={{ marginLeft: "auto", color: "var(--text-3)" }} />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const HIGHLIGHT_COLORS = [
  ["gold", "Main idea"],
  ["blue", "Question"],
  ["green", "Answer"],
  ["rose", "Emphasis"],
];

function highlightedText(text, highlights, lang, keyPrefix) {
  const value = String(text || "");
  const ranges = (highlights || [])
    .filter((item) => item.lang === lang && item.start >= 0 && item.end > item.start && item.start < value.length)
    .map((item) => ({ ...item, end: Math.min(value.length, item.end) }))
    .sort((a, b) => a.start - b.start);
  if (!ranges.length) return value;
  const nodes = [];
  let cursor = 0;
  ranges.forEach((item, index) => {
    const start = Math.max(cursor, item.start);
    if (start > cursor) nodes.push(value.slice(cursor, start));
    if (item.end > start) nodes.push(<mark className={`rs-mark rs-mark-${item.color || "gold"}`} key={`${keyPrefix}-${index}`}>{value.slice(start, item.end)}</mark>);
    cursor = Math.max(cursor, item.end);
  });
  if (cursor < value.length) nodes.push(value.slice(cursor));
  return nodes;
}

function HighlightableText({ block, text, lang, className }) {
  const ref = useRefRS(null);
  const [selection, setSelection] = useStateRS(null);
  const selectWords = () => {
    const selected = window.getSelection && window.getSelection();
    const range = selected && selected.rangeCount ? selected.getRangeAt(0) : null;
    const root = ref.current;
    if (!range || !root || range.collapsed || !root.contains(range.commonAncestorContainer)) { setSelection(null); return; }
    const before = range.cloneRange();
    before.selectNodeContents(root);
    before.setEnd(range.startContainer, range.startOffset);
    const start = before.toString().length;
    const end = start + range.toString().length;
    if (end > start) setSelection({ start, end });
  };
  const apply = (color) => {
    if (!selection) return;
    const old = block.highlights || [];
    const retained = old.filter((item) => item.lang !== lang || item.end <= selection.start || item.start >= selection.end);
    const highlights = color ? [...retained, { lang, ...selection, color }] : retained;
    window.WS.updateBlock(block.id, { highlights });
    if (window.getSelection) window.getSelection().removeAllRanges();
    setSelection(null);
  };
  return (
    <div className="rs-highlight-wrap">
      <p ref={ref} className={className} onMouseUp={() => setTimeout(selectWords, 0)} onTouchEnd={() => setTimeout(selectWords, 0)}>{highlightedText(text, block.highlights, lang, block.id)}</p>
      {selection && <div className="rs-highlight-tools" role="toolbar" aria-label="Highlight selected words">
        <span>Highlight</span>
        {HIGHLIGHT_COLORS.map(([color, label]) => <button key={color} className={`rs-swatch rs-swatch-${color}`} onClick={() => apply(color)} aria-label={label} title={label} />)}
        <button className="rs-highlight-clear" onClick={() => apply(null)}>Clear</button>
      </div>}
    </div>
  );
}

// ── a block in the editable sheet ─────────────────────────────────────────────
function SheetBlock({ block, index, total, dragHandlers, dragging, onOpenReader }) {
  const ctrls = (
    <div className="rs-block-ctrls">
      <button className="rs-ic" title="Move up" disabled={index === 0} onClick={() => window.WS.move(block.id, -1)}><Icon d="chev" size={14} style={{ transform: "rotate(-90deg)" }} /></button>
      <button className="rs-ic" title="Move down" disabled={index === total - 1} onClick={() => window.WS.move(block.id, 1)}><Icon d="chev" size={14} style={{ transform: "rotate(90deg)" }} /></button>
      <button className="rs-ic rs-del" title="Remove" onClick={() => window.WS.removeBlock(block.id)}><Icon d="close" size={14} /></button>
    </div>
  );
  const handle = <div className="rs-drag" title="Drag to reorder" {...dragHandlers}><Icon d="M5 8h14M5 12h14M5 16h14" size={15} /></div>;

  if (block.type === "heading") {
    return (
      <div className={"rs-block rs-block-heading" + (dragging ? " is-drag" : "")}>
        {handle}
        <input className="rs-heading-input" value={block.text} placeholder="Section heading…"
          onChange={(e) => window.WS.updateBlock(block.id, { text: e.target.value })} />
        {ctrls}
      </div>
    );
  }
  if (block.type === "comment") {
    const kind = TEACHING_KINDS[block.kind] || TEACHING_KINDS.note;
    return (
      <div className={`rs-block rs-block-comment rs-comment-${block.kind || "note"}${dragging ? " is-drag" : ""}`}>
        {handle}
        <div className="rs-comment-wrap">
          <Icon d={kind.icon} size={15} style={{ color: "var(--ai)", flex: "none", marginTop: 8 }} />
          <div className="rs-comment-body">
            <select className="rs-comment-kind" value={block.kind || "note"} aria-label="Teaching note type"
              onChange={(event) => window.WS.updateBlock(block.id, { kind: event.target.value })}>
              {Object.entries(TEACHING_KINDS).map(([value, item]) => <option key={value} value={value}>{item.label}</option>)}
            </select>
            <AutoText className="rs-comment-input" value={block.text} placeholder={block.kind === "question" ? "What question should the class wrestle with?" : block.kind === "answer" ? "Write the answer or resolution…" : block.kind === "note" ? "Add your comment…" : "Explain this step in the shiur…"}
              onChange={(v) => window.WS.updateBlock(block.id, { text: v })} />
          </div>
        </div>
        {ctrls}
      </div>
    );
  }
  if (block.type === "text") {
    return (
      <div className={"rs-block rs-block-source rs-block-custom" + (dragging ? " is-drag" : "")} style={{ borderLeft: "3px solid var(--gold)" }}>
        {handle}
        <div className="rs-src">
          <div className="rs-custom-cite">
            <input className="rs-custom-title" value={block.title} placeholder="Source title (e.g. Sefer HaMaamarim 5710)"
              onChange={(e) => window.WS.updateBlock(block.id, { title: e.target.value })} />
            <input className="rs-custom-loc" value={block.citation} placeholder="Citation"
              onChange={(e) => window.WS.updateBlock(block.id, { citation: e.target.value })} />
            <span className="rs-custom-tag">Custom</span>
          </div>
          <AutoText className="rs-custom-he heb" value={block.he} placeholder="העתק לשון המקור…"
            onChange={(v) => window.WS.updateBlock(block.id, { he: v })} />
          <AutoText className="rs-custom-en serif" value={block.en} placeholder="Translation / your phrasing…"
            onChange={(v) => window.WS.updateBlock(block.id, { en: v })} />
          <AutoText className="rs-src-note" value={block.note || ""} placeholder="+ Add your note on this source…"
            onChange={(v) => window.WS.updateBlock(block.id, { note: v })} />
        </div>
        {ctrls}
      </div>
    );
  }
  // source block
  const s = srcForBlock(block);
  if (!s) return null;
  return (
    <div className={"rs-block rs-block-source" + (dragging ? " is-drag" : "")} style={{ borderLeft: `3px solid ${typeVar(s.type)}` }}>
      {handle}
      <div className="rs-src">
        <div className="rs-src-head">
          <span className="srccard-n" style={{ background: typeVar(s.type) }}>{s.n}</span>
          <div className="rs-src-cite"><b>{s.title}</b> <span className="srccard-cite">{s.citation}</span>
            <span className="srccard-rel"><TypeDot type={s.type} size={6} /> {s.type} · {s.relationship}</span>
          </div>
          <div className="rs-src-actions">
            {(s.srcRef || s.citation) && <button className="rs-src-open" title="Open the original text" onClick={() => onOpenReader && onOpenReader(s.srcRef || s.citation)}><Icon d="external" size={13} /> Open</button>}
            <button className="rs-src-compare" title="Compare this source" onClick={() => window.openComparePair && window.openComparePair(s)}><Icon d="compare" size={14} /></button>
          </div>
        </div>
        <HighlightableText block={block} text={s.he} lang="he" className="heb rs-src-he" />
        <HighlightableText block={block} text={s.en} lang="en" className="rs-src-en serif" />
        <p className="rs-highlight-hint"><Icon d="spark" size={12} /> Select a few words to color-code the point.</p>
        <AutoText className="rs-src-note" value={block.note || ""} placeholder="+ Explain how this source advances the shiur…"
          onChange={(v) => window.WS.updateBlock(block.id, { note: v })} />
      </div>
      {ctrls}
    </div>
  );
}

// ── insert-anywhere bar (appears between blocks) ──────────────────────────────
function InsertBar({ index }) {
  const [open, setOpen] = useStateRS(false);
  const [pick, setPick] = useStateRS(false);
  const all = window.SOURCES || [];
  return (
    <div className={"rs-insert" + (open ? " open" : "")}>
      <div className="rs-insert-line"><button className="rs-insert-plus" onClick={() => { setOpen(!open); setPick(false); }} aria-label="Insert here"><Icon d="plus" size={14} /></button></div>
      {open && !pick && (
        <div className="rs-insert-menu">
          <button onClick={() => { window.WS.insertAt(index, "heading"); setOpen(false); }}><Icon d="text" size={14} /> Heading</button>
          <button onClick={() => { window.WS.insertAt(index, "comment", { kind: "question" }); setOpen(false); }}><Icon d="spark" size={14} /> Question</button>
          <button onClick={() => { window.WS.insertAt(index, "comment", { kind: "answer" }); setOpen(false); }}><Icon d="check" size={14} /> Answer</button>
          <button onClick={() => { window.WS.insertAt(index, "comment", { kind: "note" }); setOpen(false); }}><Icon d="note" size={14} /> Comment</button>
          <button onClick={() => { window.WS.insertAt(index, "comment", { kind: "teaching" }); setOpen(false); }}><Icon d="note" size={14} /> Teaching point</button>
          <button onClick={() => setPick(true)}><Icon d="quote" size={14} /> Source</button>
          <button onClick={() => { window.WS.insertAt(index, "text"); setOpen(false); }}><Icon d="book" size={14} /> Custom text</button>
        </div>
      )}
      {open && pick && (
        <div className="rs-insert-pick">
          {all.map((s) => (
            <button key={srcKey(s)} onClick={() => { window.WS.insertAt(index, "source", s); window.WS.collect(s); setOpen(false); }}>
              <TypeDot type={s.type} size={7} /> <span><b>{s.title}</b> {s.citation}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Discussion time, reading inside, and section transitions—not just silent reading.
function estimateMins(blocks) {
  let m = 2;
  blocks.forEach((b) => { m += b.type === "source" || b.type === "text" ? 5 : b.type === "comment" ? 2 : 0.75; });
  return Math.max(1, Math.round(m));
}

// ── outline (shiur roadmap) view ──────────────────────────────────────────────
function OutlineView({ sheet, onOpenReader }) {
  let sectionN = 0;
  return (
    <div className="rs-outline">
      <div className="rs-flow-intro">
        <span className="rs-flow-kicker">Teaching flow</span>
        <h2>How the shiur comes together</h2>
        <p>Follow the question, the source chain, and the teaching points in the order your class will encounter them.</p>
      </div>
      <div className="rs-flow-track">
      {sheet.blocks.map((b) => {
        if (b.type === "heading") { sectionN++; return (
          <div key={b.id} className="rs-out-head"><span className="rs-out-n">{sectionN}</span><div><span>Section {sectionN}</span><h3>{b.text || "Untitled section"}</h3></div></div>
        ); }
        if (b.type === "comment") {
          const kind = TEACHING_KINDS[b.kind] || TEACHING_KINDS.note;
          return (
            <div key={b.id} className={`rs-out-point rs-flow-${b.kind || "note"}`}>
              <span className="rs-flow-dot"><Icon d={kind.icon} size={13} /></span>
              <div><b className="rs-flow-label">{kind.label}</b><p>{b.text || "…"}</p></div>
            </div>
          );
        }
        if (b.type === "text") return (
          <div key={b.id} className="rs-out-src">
            <span className="rs-flow-dot"><TypeDot type="Chassidus" size={7} /></span>
            <div><span className="rs-out-cite"><b>{b.title || "Custom source"}</b> {b.citation}</span>
            {b.note && <span className="rs-out-note">{b.note}</span>}</div>
          </div>
        );
        const s = srcForBlock(b); if (!s) return null;
        return (
          <div key={b.id} className="rs-out-src">
            <span className="rs-flow-dot"><TypeDot type={s.type} size={7} /></span>
            <div className="rs-out-src-body">
              <span className="rs-flow-label">{s.relationship || "Source"}</span>
              <span className="rs-out-cite"><b>{s.title}</b> {s.citation}</span>
              {b.note && <span className="rs-out-note">{b.note}</span>}
            </div>
            {(s.srcRef || s.citation) && <button className="rs-flow-open" onClick={() => onOpenReader && onOpenReader(s.srcRef || s.citation)}><Icon d="external" size={13} /> Original</button>}
          </div>
        );
      })}
      </div>
    </div>
  );
}

// ── print document for the source sheet ───────────────────────────────────────
function ResearchPrintDoc({ ws, mode }) {
  const sheet = window.activeSheet(ws);
  let sectionN = 0;
  const node = (
    <div className="print-doc rs-print" data-direction="beis">
      <div className="pd-masthead"><span className="pd-brand">מַעְיָנוֹת · MAYANOS</span><span className="pd-brand-sub">{mode === "outline" ? "Shiur outline" : "Source sheet"}</span></div>
      <header className="pd-title"><h2 className="pd-en" style={{ borderLeft: "none", paddingLeft: 0 }}>{sheet.title}</h2></header>
      <article className="pd-body">
        {sheet.blocks.map((b) => {
          if (b.type === "heading") { sectionN++; return <h3 key={b.id} className="rsp-head">{sectionN}. {b.text}</h3>; }
          if (b.type === "comment") return <div key={b.id} className="rsp-comment"><b>{(TEACHING_KINDS[b.kind] || TEACHING_KINDS.note).label}</b><p>{b.text}</p></div>;
          if (b.type === "text") return (
            <div key={b.id} className="rsp-src">
              <div className="rsp-cite"><b>{b.title || "Custom source"}</b> {b.citation}</div>
              {mode !== "outline" && b.he && <p lang="he" dir="rtl" className="heb rsp-he">{b.he}</p>}
              {mode !== "outline" && b.en && <p className="rsp-en">{b.en}</p>}
              {b.note && <p className="rsp-note">{b.note}</p>}
            </div>
          );
          const s = srcForBlock(b); if (!s) return null;
          return (
            <div key={b.id} className="rsp-src">
              <div className="rsp-cite"><b>{s.n}. {s.title}</b> {s.citation} <span className="pd-note-rel">— {s.type}</span></div>
              {mode !== "outline" && <p lang="he" dir="rtl" className="heb rsp-he">{highlightedText(s.he, b.highlights, "he", `print-he-${b.id}`)}</p>}
              {mode !== "outline" && <p className="rsp-en">{highlightedText(s.en, b.highlights, "en", `print-en-${b.id}`)}</p>}
              {b.note && <p className="rsp-note">{b.note}</p>}
            </div>
          );
        })}
      </article>
      <footer className="pd-colophon">Prepared in Mayanos · {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</footer>
    </div>
  );
  return ReactDOM.createPortal(node, document.body);
}

// Small dropdown helper (click-scrim to close)
function Dropdown({ open, onClose, children, align }) {
  if (!open) return null;
  return (
    <>
      <div className="rs-dd-scrim" onClick={onClose} />
      <div className={"rs-dd" + (align === "right" ? " rs-dd-right" : "")}>{children}</div>
    </>
  );
}

function SheetsMenu({ ws, isMobile }) {
  const [open, setOpen] = useStateRS(false);
  const sheets = ws.sheets;
  return (
    <div className="rs-menuwrap">
      <button className="rs-sheets-btn" onClick={() => setOpen(!open)}>
        <Icon d="layers" size={15} /> {isMobile ? sheets.length : <>{sheets.length} sheet{sheets.length !== 1 ? "s" : ""}</>}
        <Icon d="chevd" size={13} style={{ opacity: .6 }} />
      </button>
      <Dropdown open={open} onClose={() => setOpen(false)}>
        <div className="rs-dd-h">Your source sheets</div>
        <div className="rs-dd-list">
          {sheets.map((sh) => (
            <div key={sh.id} className={"rs-dd-item" + (sh.id === ws.activeId ? " on" : "")}>
              <button className="rs-dd-pick" onClick={() => { window.WS.switchSheet(sh.id); setOpen(false); }}>
                <span className="rs-dd-check">{sh.id === ws.activeId && <Icon d="check" size={13} />}</span>
                <span className="rs-dd-name">{sh.title || "Untitled"}<i>{sh.blocks.filter((b) => b.type === "source").length} sources</i></span>
              </button>
              <button className="rs-dd-ic" title="Duplicate" onClick={() => { window.WS.duplicateSheet(sh.id); setOpen(false); }}><Icon d="layers" size={13} /></button>
              <button className="rs-dd-ic rs-dd-del" title="Delete" onClick={() => window.WS.deleteSheet(sh.id)}><Icon d="close" size={13} /></button>
            </div>
          ))}
        </div>
        <button className="rs-dd-new" onClick={() => { window.WS.newSheet(); setOpen(false); }}><Icon d="plus" size={15} /> New source sheet</button>
      </Dropdown>
    </div>
  );
}

function ExportMenu({ isMobile }) {
  const [open, setOpen] = useStateRS(false);
  return (
    <div className="rs-menuwrap">
      <button className="btn" onClick={() => setOpen(!open)}><Icon d="external" size={15} /> {!isMobile && "Export"} <Icon d="chevd" size={12} style={{ opacity: .5 }} /></button>
      <Dropdown open={open} onClose={() => setOpen(false)} align="right">
        <div className="rs-dd-h">Export as Word / Docs</div>
        <button className="rs-dd-row" onClick={() => { window.WS.exportDoc("sheet"); setOpen(false); }}>
          <Icon d="text" size={15} /><span><b>Source sheet (.doc)</b><i>Full mekoros + your notes</i></span>
        </button>
        <button className="rs-dd-row" onClick={() => { window.WS.exportDoc("outline"); setOpen(false); }}>
          <Icon d="path" size={15} /><span><b>Shiur outline (.doc)</b><i>Headings, points & citations</i></span>
        </button>
        <div className="rs-dd-note">Opens in Microsoft Word or Google Docs for further editing.</div>
      </Dropdown>
    </div>
  );
}

function ResearchModal({ onClose, children, wide, label }) {
  useEffectRS(() => {
    const escape = (event) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", escape);
    return () => window.removeEventListener("keydown", escape);
  }, [onClose]);
  return (
    <div className="rs-modal-scrim" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className={`rs-modal${wide ? " rs-modal-wide" : ""}`} role="dialog" aria-modal="true" aria-label={label || "Research dialog"}>
        <button className="rs-modal-close" onClick={onClose} aria-label="Close"><Icon d="close" size={18} /></button>
        {children}
      </section>
    </div>
  );
}

function SampleCards({ onLoad, loading }) {
  return (
    <div className="rs-samples-grid">
      {SAMPLE_SHIURIM.map((sample) => (
        <article className="rs-sample-card" key={sample.id}>
          <div className="rs-sample-top"><span>{sample.eyebrow}</span><i>{sample.duration}</i></div>
          <h3>{sample.title}</h3>
          <p>{sample.summary}</p>
          <div className="rs-sample-chain" aria-label={`${sample.sources.length} real sources`}>
            {sample.sources.map((source, index) => <React.Fragment key={source.ref}><span>{source.type}</span>{index < sample.sources.length - 1 && <Icon d="chev" size={11} />}</React.Fragment>)}
          </div>
          <button className="btn" disabled={!!loading} onClick={() => onLoad(sample)}>
            <Icon d={loading === sample.id ? "spark" : "layers"} size={15} /> {loading === sample.id ? "Loading real texts…" : "Use this sample"}
          </button>
        </article>
      ))}
    </div>
  );
}

function TeacherGuide({ onClose, onLoadSample, loading, onOpenReader }) {
  return (
    <ResearchModal onClose={onClose} wide label="How to build and share a shiur">
      <div className="rs-guide-head">
        <span className="rs-eyebrow">Teacher studio walkthrough</span>
        <h2>Build a shiur that shows the whole chain</h2>
        <p>Begin with the text you are teaching, bring its mekoros inside, then arrange the question, sources, and takeaway in the order your class needs.</p>
      </div>
      <video className="rs-guide-video" controls preload="metadata" poster="/assets/research-walkthrough-poster.png">
        <source src="/assets/research-walkthrough.mp4" type="video/mp4" />
      </video>
      <div className="rs-guide-video-note"><Icon d="play" size={14} /> Silent visual walkthrough · 58 seconds · made with Remotion</div>
      <div className="rs-guide-steps">
        {[
          ["1", "Start with a text", "Open a Sicha or maamar in the Reader and choose its sources."],
          ["2", "Build the chain", "Add the original source, an opening question, teaching points, and transitions."],
          ["3", "Check the flow", "Switch to Teaching flow to see how every source advances the shiur."],
          ["4", "Teach or share", "Print, export to Word/Docs, or send a link that opens a separate copy."],
        ].map(([number, title, text]) => <div className="rs-guide-step" key={number}><b>{number}</b><div><h3>{title}</h3><p>{text}</p></div></div>)}
      </div>
      <div className="rs-guide-action"><button className="btn btn-primary" onClick={() => { onClose(); onOpenReader(); }}><Icon d="book" size={15} /> Open the Reader</button></div>
      <div className="rs-guide-samples">
        <span className="rs-eyebrow">Learn from a finished example</span>
        <h2>Three real source chains</h2>
        <p>Every Hebrew and English source below is loaded from the Mayanos corpus when you choose it. Your current work remains untouched.</p>
        <SampleCards onLoad={onLoadSample} loading={loading} />
      </div>
    </ResearchModal>
  );
}

function ShareDialog({ url, onClose, onCopy }) {
  const nativeShare = async () => {
    if (!navigator.share) return onCopy();
    try { await navigator.share({ title: "Mayanos shiur", text: "Open this source sheet and teaching flow in Mayanos.", url }); } catch (error) {}
  };
  return (
    <ResearchModal onClose={onClose} label="Share this shiur">
      <div className="rs-share-dialog">
        <div className="rs-share-icon"><Icon d="link" size={24} /></div>
        <span className="rs-eyebrow">Ready to teach</span>
        <h2>Your shiur link is ready</h2>
        <p>Anyone with this link can open the source sheet and teaching flow. They receive their own editable copy—your original and the rest of your workspace stay private.</p>
        <div className="rs-share-field"><input readOnly value={url} onFocus={(event) => event.target.select()} aria-label="Share link" /><button onClick={onCopy}>Copy</button></div>
        <div className="rs-share-actions">
          <button className="btn btn-primary" onClick={onCopy}><Icon d="link" size={15} /> Copy link</button>
          {navigator.share && <button className="btn" onClick={nativeShare}><Icon d="external" size={15} /> Share…</button>}
        </div>
        <span className="rs-share-expiry">The link expires after 90 days.</span>
      </div>
    </ResearchModal>
  );
}

function Research({ onOpenReader, onOpenCompare }) {
  const ws = window.useWorkspace();
  const account = window.useMayanosAccount ? window.useMayanosAccount() : { ready: false, authenticated: false, message: "Saved only on this device" };
  const sheet = window.activeSheet(ws);
  const [view, setView] = useStateRS("sheet");   // sheet | outline
  const [printMode, setPrintMode] = useStateRS("sheet");
  const [addOpen, setAddOpen] = useStateRS(false); // mobile add panel
  const [shareState, setShareState] = useStateRS("");
  const [shareUrl, setShareUrl] = useStateRS("");
  const [guideOpen, setGuideOpen] = useStateRS(false);
  const [sampleLoading, setSampleLoading] = useStateRS("");
  const dragFrom = useRefRS(null);
  const [dragId, setDragId] = useStateRS(null);
  const isMobile = window.useIsMobile();

  useEffectRS(() => {
    const id = new URLSearchParams(location.search).get("sheet");
    if (!id || sessionStorage.getItem(`mayanos-shared-${id}`)) return;
    fetch(`/bff/workspaces/${encodeURIComponent(id)}`)
      .then((response) => response.ok ? response.json() : Promise.reject(new Error("Shared sheet not found")))
      .then((payload) => {
        if (window.WS.importShared(payload)) {
          sessionStorage.setItem(`mayanos-shared-${id}`, "1");
          setShareState("Shared copy opened");
        }
      })
      .catch(() => setShareState("Shared sheet unavailable"));
  }, []);

  const shareSheet = async () => {
    setShareState("Creating link…");
    try {
      const response = await fetch("/bff/workspaces", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(window.WS.sharePayload()),
      });
      const result = await response.json();
      if (!response.ok || !result.id) throw new Error(result.error || "Unable to share");
      const url = `${location.origin}/research?sheet=${encodeURIComponent(result.id)}`;
      setShareUrl(url);
      let copied = false;
      try { await navigator.clipboard.writeText(url); copied = true; } catch (error) {}
      if (!copied) {
        const field = document.createElement("textarea");
        field.value = url;
        field.setAttribute("readonly", "");
        field.style.position = "fixed";
        field.style.left = "-1000px";
        document.body.appendChild(field);
        field.select();
        try { copied = document.execCommand("copy"); } catch (error) {}
        field.remove();
      }
      setShareState(copied ? "Share link copied" : "Share link created");
      if (window.MAYANOS_ANALYTICS) {
        window.MAYANOS_ANALYTICS.track("workspace_shared", {
          title: sheet.title || "",
          blocks: sheet.blocks.length,
        }, { workspaceSnapshot: window.WS.get() });
      }
    } catch (error) {
      setShareState(error && error.message ? error.message : "Unable to create share link");
    }
  };

  const copyShareUrl = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareState("Share link copied");
    } catch (error) {
      const field = document.createElement("textarea");
      field.value = shareUrl; document.body.appendChild(field); field.select();
      try { document.execCommand("copy"); setShareState("Share link copied"); } catch (copyError) {}
      field.remove();
    }
  };

  const loadSample = async (sample) => {
    if (!sample || sampleLoading) return;
    setSampleLoading(sample.id);
    setShareState(`Loading ${sample.title} from the corpus…`);
    try {
      const sources = await hydrateSample(sample);
      if (!window.WS.createGuidedSheet(sample, sources)) throw new Error("Could not create the sample shiur");
      setGuideOpen(false);
      setView("outline");
      setShareState(`${sample.title} opened as a new sheet`);
    } catch (error) {
      setShareState(error && error.message ? error.message : "Could not load this sample");
    } finally {
      setSampleLoading("");
    }
  };

  const doPrint = (mode) => {
    if (window.MAYANOS_ANALYTICS) window.MAYANOS_ANALYTICS.track("print_started", { surface: "research", mode });
    setPrintMode(mode);
    setTimeout(() => window.print(), 80);
  };

  const hasBlocks = sheet.blocks.length > 0;
  const openSourceFromResearch = (ref) => onOpenReader && onOpenReader(ref, {
    origin: {
      url: location.pathname + location.search,
      ref: "",
      segmentRef: "",
      scrollTop: window.scrollY || 0,
      title: sheet.title || "your shiur",
    },
  });

  const makeDragHandlers = (index, id) => ({
    draggable: true,
    onDragStart: (e) => { dragFrom.current = index; setDragId(id); e.dataTransfer.effectAllowed = "move"; },
    onDragEnd: () => { dragFrom.current = null; setDragId(null); },
  });
  const onRowDragOver = (e) => e.preventDefault();
  const onRowDrop = (index) => (e) => { e.preventDefault(); if (dragFrom.current != null) window.WS.reorder(dragFrom.current, index); dragFrom.current = null; setDragId(null); };

  return (
    <div className="research">
      <p className="rs-beta-note"><strong>Research · Beta</strong><span>Collect sources and build source sheets. This feature is still being refined, so tools and layouts may change.</span></p>
      <div className="rs-bar">
        <div className="rs-bar-l">
          <SheetsMenu ws={ws} isMobile={isMobile} />
          <div className="rs-title-wrap">
            <input className="rs-title" value={sheet.title} onChange={(e) => window.WS.setTitle(e.target.value)} aria-label="Sheet title" />
            <span className="rs-bar-meta">{sheet.blocks.filter((b) => b.type === "source" || b.type === "text").length} sources · {sheet.blocks.filter((b) => b.type === "heading").length} sections · ~{estimateMins(sheet.blocks)} min</span>
          </div>
        </div>
        <div className="rs-bar-r">
          <button className="btn rs-guide-btn" onClick={() => setGuideOpen(true)}><Icon d="spark" size={15} /> {!isMobile && "Guide & examples"}</button>
          <div className="rs-viewtog">
            {[["sheet", "Source sheet", "text"], ["outline", "Teaching flow", "path"]].map(([k, l, ic]) => (
              <button key={k} className={"rs-viewbtn" + (view === k ? " on" : "")} onClick={() => setView(k)}><Icon d={ic} size={14} /> {!isMobile && l}</button>
            ))}
          </div>
          <button className="btn" onClick={shareSheet} disabled={!hasBlocks}><Icon d="link" size={15} /> {!isMobile && "Share shiur"}</button>
          <ExportMenu isMobile={isMobile} />
          <button className="btn" disabled={!hasBlocks} onClick={() => doPrint(view)}><Icon d="printer" size={15} /> {!isMobile && "Print"}</button>
        </div>
      </div>
      {shareState && <div className="rs-dd-note" role="status" style={{ textAlign: "right", paddingInline: 18 }}>{shareState}</div>}
      <div className="rs-dd-note" role="status" style={{ textAlign: "right", paddingInline: 18 }}>
        {account.authenticated ? `${account.message} · ${account.displayName || "Signed in"}` : <>{account.message}. <a href={account.signIn || "/signin-with-chatgpt"}>Sign in with ChatGPT to sync across devices</a>.</>}
      </div>

      <div className={`rs-grid${isMobile ? " rs-grid-mobile" : ""}${!hasBlocks ? " rs-grid-empty" : ""}`}>
        {!isMobile && <CollectRail ws={ws} onOpenReader={openSourceFromResearch} onNotice={setShareState} />}

        <section className="rs-main">
          {isMobile && (
            <div className="rs-mobile-add">
              <button className="btn" style={{ width: "100%", justifyContent: "center" }} onClick={() => setAddOpen(true)}><Icon d="plus" size={16} /> Add sources, headings & notes</button>
            </div>
          )}
          {view === "sheet" ? (
            <div className="rs-sheet">
              {!hasBlocks && <section className="rs-desk-empty">
                <span className="rs-eyebrow">Source sheets</span>
                <h1>Create a source sheet.</h1>
                <p>Find a source, add your notes, and arrange them in the order you want to teach.</p>
                <div className="rs-desk-paths">
                  <button onClick={() => { if (isMobile) setAddOpen(true); else { const field = document.querySelector(".rs-findbox input"); if (field) field.focus(); } }}><Icon d="search" size={16} /><span><b>Find a source</b><i>Search by idea, phrase, title, or reference</i></span></button>
                  <button onClick={() => window.WS.insertAt(0, "comment", { kind: "note" })}><Icon d="note" size={16} /><span><b>Add a note</b><i>Write a question, explanation, or teaching point</i></span></button>
                </div>
              </section>}
              <InsertBar index={0} />
              {sheet.blocks.map((b, i) => (
                <React.Fragment key={b.id}>
                  <div onDragOver={onRowDragOver} onDrop={onRowDrop(i)}>
                    <SheetBlock block={b} index={i} total={sheet.blocks.length} onOpenReader={openSourceFromResearch}
                      dragHandlers={makeDragHandlers(i, b.id)} dragging={dragId === b.id} />
                  </div>
                  <InsertBar index={i + 1} />
                </React.Fragment>
              ))}
              <div className="rs-sheet-foot">
                <button className="rs-foot-add" onClick={() => window.WS.addHeading()}><Icon d="text" size={14} /> Heading</button>
                <button className="rs-foot-add" onClick={() => window.WS.addComment("", "question")}><Icon d="spark" size={14} /> Question</button>
                <button className="rs-foot-add" onClick={() => window.WS.addComment("", "answer")}><Icon d="check" size={14} /> Answer</button>
                <button className="rs-foot-add" onClick={() => window.WS.addComment("", "note")}><Icon d="note" size={14} /> Comment</button>
                {hasBlocks && <button className="rs-foot-add rs-foot-clear" onClick={() => window.WS.clear()}><Icon d="close" size={14} /> Clear sheet</button>}
              </div>
            </div>
          ) : (
            <OutlineView sheet={sheet} onOpenReader={openSourceFromResearch} />
          )}
        </section>
      </div>

      {/* mobile add panel */}
      {isMobile && addOpen && (
        <>
          <div className="sheet-scrim on" style={{ zIndex: 70 }} onClick={() => setAddOpen(false)} />
          <div className="rs-add-sheet">
            <div className="sheet-grip" onClick={() => setAddOpen(false)}><span /><button className="sheet-close" onClick={() => setAddOpen(false)}><Icon d="chevd" size={18} /></button></div>
            <div className="rs-add-sheet-body"><CollectRail ws={ws} onClose={() => setAddOpen(false)} isMobile onOpenReader={openSourceFromResearch} onNotice={setShareState} /></div>
          </div>
        </>
      )}

      {guideOpen && <TeacherGuide onClose={() => setGuideOpen(false)} onLoadSample={loadSample} loading={sampleLoading} onOpenReader={onOpenReader} />}
      {shareUrl && <ShareDialog url={shareUrl} onClose={() => setShareUrl("")} onCopy={copyShareUrl} />}

      <ResearchPrintDoc ws={ws} mode={printMode} />
    </div>
  );
}

Object.assign(window, { Research });
